import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextInput from '../../components/TextInput';
import React, { useState } from 'react';
import { useRulesContext } from '../../stores/rules-context';
import { Identifier } from '../../types/identifier';
import { JobRule } from '../../types/rule';
import useConfigStyles from '../../components/useConfigStyles';

type JobRuleSelectProps = {
  rules: JobRule[];
  addRule: (rule: JobRule) => void;
};

const DefaultSelectRule: Identifier = {
  Id: '0',
  Name: '',
};

const severity = ['1-ERROR', '2-WARNING', '3-INFORMATION'];

function JobRuleSelect({ rules, addRule }: JobRuleSelectProps): JSX.Element {
  const [selectedRule, setSelectedRule] = useState<Identifier>(DefaultSelectRule);
  const [jobRuleSeverity, setJobRuleSeverity] = useState(severity[0]);
  const [jobRuleEnabled, setJobRuleEnabled] = useState(true);
  const [buttonLabel, setButtonLabel] = useState('Add');
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const rulesContext = useRulesContext();
  const classes = useConfigStyles();

  const updateButton = (ruleName: string) => {
    if (rules.findIndex((v) => v.Name === ruleName) >= 0) {
      setButtonLabel('Update');
    } else {
      setButtonLabel('Add');
    }
    setButtonEnabled(ruleName !== '0');
  };

  const handleRuleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSelectedRule({ Id: value, Name: value });
    updateButton(value);
  };

  const handleSeveritySelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setJobRuleSeverity(value);
  };

  const handleOnClick = () => {
    const ruleToAddOrUpdate = rulesContext.data.find((r) => r.Name === selectedRule.Name);
    if (ruleToAddOrUpdate) {
      addRule({
        ...ruleToAddOrUpdate,
        Severity: jobRuleSeverity,
        Disabled: !jobRuleEnabled,
      });
      setSelectedRule(DefaultSelectRule);
      setJobRuleSeverity(severity[0]);
      setJobRuleEnabled(true);
      setButtonEnabled(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setJobRuleEnabled(event.target.checked);
  };

  return (
    <Grid container spacing={2} justifyContent='flex-end' alignItems='center' className={classes.container}>
      <Grid item md={10}>
        <TextInput name='Rules' label='Rules' value={selectedRule.Id} onChange={handleRuleSelect} select>
          <MenuItem value='0'>Select...</MenuItem>
          {rulesContext.data.map((rule) => {
            return (
              <MenuItem key={rule.Name} value={rule.Name}>
                {rule.Name}
              </MenuItem>
            );
          })}
        </TextInput>
      </Grid>
      <Grid item md={5}>
        <TextInput name='Severity' label='Severity' value={jobRuleSeverity} onChange={handleSeveritySelect} select>
          {severity.map((s) => {
            return (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            );
          })}
        </TextInput>
      </Grid>
      <Grid item md={3}>
        <InputLabel>Enabled</InputLabel>
        <Switch checked={jobRuleEnabled} onChange={handleChange} color='primary' name='Enabled' />
      </Grid>
      <Grid item md={2}>
        <Button variant='contained' color='primary' type='button' onClick={handleOnClick} disabled={!buttonEnabled}>
          {buttonLabel}
        </Button>
      </Grid>
    </Grid>
  );
}

export default JobRuleSelect;
